import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Tabs,
  Tab,
} from "react-bootstrap";
import moment from "moment";
import {
  getTicketThread,
  getTasksByTicketId,
  getTicketByTicketId,
  getBuildingBlockByTicket,
  getTicketMetaNames,
  getAllTicketStatuses,
} from "../Api/TicketApiOrg";
import { useAuth } from "../Context/AuthContext";
import TicketEditModal from "./TicketEditModal";
import "./TicketInfoPage.css";
import { attachmentApi } from "../Api/TicketApiOrg";
import { toast } from "react-toastify";
import { Accordion } from "react-bootstrap";
import { addTicketRemark, getTicketRemarks } from "../Api/TicketApiOrg";
import { getResourceDetails, fetchResourceById } from "../Api/ResourceMaster";
import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
import { getTeamById } from "../Api/TeamMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { getPartnersByProject } from "../Api/PartnerMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchProjectsByOrg } from "../Api/ViewProjects";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { resolveNamesApi } from "../Api/TicketApiOrg";
import { getContactNames } from "../Api/TicketApiOrg";

/* ================= HELPERS ================= */

const getPriorityBadge = (priority) => {
  const variants = {
    critical: "danger",
    high: "warning",
    medium: "primary",
    low: "secondary",
    normal: "info",
  };

  return (
    <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
      {priority || "N/A"}
    </Badge>
  );
};

const formatDateTime = (date, time) => {
  if (!date) return "N/A";
  return `${moment(date).format("MMM DD, YYYY")} ${
    time?.substring(0, 8) || ""
  }`;
};

/* ================= PAGE ================= */

const TicketInfoPage = () => {
  const { ticketId } = useParams();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [ticketTasks, setTicketTasks] = useState([]);

  const [ticketDetails, setTicketDetails] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const [buildingBlock, setBuildingBlock] = useState(null);

  /* SAFE STATES FOR MODAL */
  const [projectOptions, setProjectOptions] = useState([]);
  const [ticketAssignees, setTicketAssignees] = useState([]);

  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  /* State for user names mapping (by mavUserId) */
  const [userNamesMap, setUserNamesMap] = useState({});

  /* State for resource names mapping (by resourceId like SSMAHA0007) */
  const [resourceNamesMap, setResourceNamesMap] = useState({});

  /* State for CusVen CP names mapping */
  const [cusVenCpCache, setCusVenCpCache] = useState({});

  /* State for contact names (GUIDs from CusVenCp) */
  const [contactNamesCache, setContactNamesCache] = useState({});

  const [assignedToOptions, setAssignedToOptions] = useState([]);

  const [projectName, setProjectName] = useState("");

  const [assigneeOptionsByProject, setAssigneeOptionsByProject] = useState({});

  // State for resolved meta names (Type, Reason, Category)
  const [resolvedNames, setResolvedNames] = useState({
    types: {},
    reasons: {},
    categories: {},
  });

  const [statusMasterData, setStatusMasterData] = useState({
    statusMap: {},
    subStatusMap: {},
    allStatuses: [],
  });

  // Helper to check if a string is a valid GUID
  const isValidGuid = (guid) => {
    if (!guid || typeof guid !== "string") return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      guid,
    );
  };

  // Resolve ticket type from GUID to name
  const resolveTicketType = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.types[key] || guid.substring(0, 8);
  };

  // Resolve ticket reason from GUID to name
  const resolveTicketReason = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.reasons[key] || guid.substring(0, 8);
  };

  // Resolve category from GUID to name
  const resolveCategoryName = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.categories[key] || guid.substring(0, 8);
  };

  // Resolve status from GUID to name
  const resolveStatusName = (statusGuid) => {
    if (!statusGuid) return "N/A";
    if (!isValidGuid(statusGuid)) return statusGuid;
    return (
      statusMasterData.statusMap[statusGuid.toLowerCase()] ||
      statusGuid.substring(0, 8)
    );
  };

  // Resolve sub-status from GUID to name
  const resolveSubStatusName = (subStatusGuid) => {
    if (!subStatusGuid) return "N/A";
    if (!isValidGuid(subStatusGuid)) return subStatusGuid;
    return (
      statusMasterData.subStatusMap[subStatusGuid.toLowerCase()] ||
      subStatusGuid.substring(0, 8)
    );
  };

  // ============== FIXED: Resolve contact name (handles all types) ==============
  const resolveContactName = (guid) => {
    if (!guid) return "N/A";

    // Check if it's an email address
    if (guid.includes("@")) return guid;

    // Check if it's a regular name (contains space, not a CVCP ID)
    if (guid.includes(" ") && !guid.startsWith("CVCP")) return guid;

    // Check if it's a CVCP ID
    if (guid.startsWith("CVCP")) {
      return cusVenCpCache[guid] || guid;
    }

    // Check if it's a GUID format (contact person from CusVenCp)
    if (isValidGuid(guid)) {
      const contactName = contactNamesCache[guid.toLowerCase()];
      if (contactName) return contactName;
      // If not found, return truncated GUID
      return guid.substring(0, 8);
    }

    // Check if it's a numeric user ID (mavUserId)
    const numericId = parseInt(guid);
    if (!isNaN(numericId)) {
      return userNamesMap[numericId] || `User ${guid}`;
    }

    // Check if it's a resource ID (like SSMAHA0007)
    if (resourceNamesMap[guid]) return resourceNamesMap[guid];

    // Return as is if nothing matches
    return guid;
  };

  // Resolve multiple assignees (handles comma-separated values)
  const resolveAssigneeNames = (ids) => {
    if (!ids) return "N/A";
    const idArray =
      typeof ids === "string" ? ids.split(",").map((i) => i.trim()) : ids;
    return idArray.map((id) => resolveContactName(id)).join(", ");
  };

  // Get status badge with proper name
  const getStatusBadgeWithName = (statusGuid) => {
    const statusName = resolveStatusName(statusGuid);
    const variants = {
      Open: "success",
      "In Progress": "warning",
      Resolved: "info",
      Closed: "secondary",
    };
    const variant = variants[statusName] || "secondary";
    return <Badge bg={variant}>{statusName}</Badge>;
  };

  // Get sub-status badge
  const getSubStatusBadge = (subStatusGuid) => {
    const subStatusName = resolveSubStatusName(subStatusGuid);
    return <Badge bg="secondary">{subStatusName}</Badge>;
  };

  // Fetch meta names (Ticket Type, Reason, Category)
  const resolveAllTicketNames = useCallback(async (ticketData) => {
    if (!ticketData) return;

    const typeSet = new Set();
    const reasonSet = new Set();
    const categorySet = new Set();

    if (ticketData.ticketType && isValidGuid(ticketData.ticketType)) {
      typeSet.add(ticketData.ticketType);
    }
    if (ticketData.ticketReason && isValidGuid(ticketData.ticketReason)) {
      reasonSet.add(ticketData.ticketReason);
    }
    if (ticketData.category && isValidGuid(ticketData.category)) {
      categorySet.add(ticketData.category);
    }

    if (typeSet.size === 0 && reasonSet.size === 0 && categorySet.size === 0) {
      return;
    }

    try {
      const typeGuidsStr =
        typeSet.size > 0 ? Array.from(typeSet).join(",") : null;
      const reasonGuidsStr =
        reasonSet.size > 0 ? Array.from(reasonSet).join(",") : null;
      const categoryGuidsStr =
        categorySet.size > 0 ? Array.from(categorySet).join(",") : null;

      const data = await getTicketMetaNames(
        typeGuidsStr,
        reasonGuidsStr,
        categoryGuidsStr,
      );

      const typeMap = {};
      const reasonMap = {};
      const categoryMap = {};

      data.forEach((item) => {
        const key = item.guid.toLowerCase();
        if (item.entity === "TYPE") typeMap[key] = item.name;
        if (item.entity === "LOG") reasonMap[key] = item.name;
        if (item.entity === "CATEGORY") categoryMap[key] = item.name;
      });

      setResolvedNames({
        types: typeMap,
        reasons: reasonMap,
        categories: categoryMap,
      });
    } catch (err) {
      console.error("Failed to resolve meta names", err);
    }
  }, []);

  // Load status master data
  const loadStatusMasterData = async () => {
    try {
      const statuses = await getAllTicketStatuses();
      const statusMap = {};
      const subStatusMap = {};

      statuses.forEach((mainStatus) => {
        statusMap[mainStatus.statusGuid.toLowerCase()] = mainStatus.statusName;
        if (mainStatus.subStatuses && mainStatus.subStatuses.length > 0) {
          mainStatus.subStatuses.forEach((subStatus) => {
            subStatusMap[subStatus.statusGuid.toLowerCase()] =
              subStatus.statusName;
          });
        }
      });

      setStatusMasterData({
        statusMap,
        subStatusMap,
        allStatuses: statuses,
      });
    } catch (error) {
      console.error("Failed to load status master data:", error);
    }
  };

  // Fetch contact names for GUIDs
  const fetchContactNames = async (contactGuids) => {
    if (!contactGuids || contactGuids.length === 0) return;

    const guidsToFetch = contactGuids.filter(
      (guid) =>
        guid &&
        !contactNamesCache[guid.toLowerCase()] &&
        guid.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        ),
    );

    if (guidsToFetch.length === 0) return;

    try {
      const guidString = guidsToFetch.join(",");

      // ✅ USE SAME API AS TABLE PAGE
      const contacts = await getContactNames(guidString);

      const newCache = { ...contactNamesCache };

      contacts.forEach((c) => {
        if (c.contactGuid && c.contactName) {
          newCache[c.contactGuid.toLowerCase()] = c.contactName;
        }
      });

      setContactNamesCache(newCache);
    } catch (error) {
      console.error("Failed to fetch contact names:", error);
    }
  };

  // Fetch resource user names (by numeric userId)
  const fetchResourceUserNames = async (userIds) => {
    try {
      const uniqueUserIds = [...new Set(userIds)];
      const userPromises = uniqueUserIds.map(async (userId) => {
        try {
          const response = await getResourceDetails({ mavUserId: userId });
          if (response?.resource) {
            return { userId, name: response.resource.name };
          }
          return { userId, name: `User ${userId}` };
        } catch (error) {
          return { userId, name: `User ${userId}` };
        }
      });

      const userResults = await Promise.all(userPromises);
      const namesMap = {};
      userResults.forEach(({ userId, name }) => {
        namesMap[userId] = name;
      });
      setUserNamesMap((prev) => ({ ...prev, ...namesMap }));
    } catch (error) {
      console.error("Failed to fetch user names:", error);
    }
  };

  // Fetch resource names (by resourceId)
  const fetchResourceNames = async (resourceIds) => {
    try {
      const uniqueResourceIds = [
        ...new Set(
          resourceIds.filter(
            (id) =>
              id &&
              !id.startsWith("CVCP") &&
              !id.includes("@") &&
              !id.includes(" ") &&
              isNaN(Number(id)),
          ),
        ),
      ];

      const resourcePromises = uniqueResourceIds.map(async (resourceId) => {
        try {
          const response = await fetchResourceById(resourceId);
          if (response?.resource?.name) {
            return { resourceId, name: response.resource.name };
          }
          return { resourceId, name: resourceId };
        } catch (error) {
          return { resourceId, name: resourceId };
        }
      });

      const resourceResults = await Promise.all(resourcePromises);
      const namesMap = {};
      resourceResults.forEach(({ resourceId, name }) => {
        namesMap[resourceId] = name;
      });
      setResourceNamesMap((prev) => ({ ...prev, ...namesMap }));
    } catch (error) {
      console.error("Failed to fetch resource names:", error);
    }
  };

  // Resolve CVCP names
  const resolveCusVenCpName = async (cpId) => {
    if (!cpId || !cpId.startsWith("CVCP")) return null;
    if (cusVenCpCache[cpId]) return cusVenCpCache[cpId];

    try {
      const cp = await fetchCusVenCpById(cpId);
      if (cp?.contactPerName) {
        setCusVenCpCache((prev) => ({ ...prev, [cpId]: cp.contactPerName }));
        return cp.contactPerName;
      }
    } catch (err) {
      console.error("Failed to fetch CP:", cpId, err);
    }
    return cpId;
  };

  // Build assigned to options
  const buildAssignedToOptions = async () => {
    try {
      if (!user?.organisationId) return;
      const res = await getResourcesByOrgId(user.organisationId);
      const resources = Array.isArray(res) ? res : res?.resources || [];
      const options = resources.map((r) => ({
        value: r.resourceId,
        label: r.name,
      }));
      setAssignedToOptions(options);
    } catch (error) {
      console.error("Failed to load assignees", error);
    }
  };

  // Fetch project and partners for assignees
  const fetchProjectAndPartners = async (projectId) => {
    try {
      if (assigneeOptionsByProject[projectId]) return;

      const localOptions = [];
      const projectDetails = await fetchProjectDetails(projectId);

      if (projectDetails?.teamAssigned) {
        const teamData = await getTeamById(projectDetails.teamAssigned);
        if (Array.isArray(teamData?.teamMembers)) {
          for (const memberId of teamData.teamMembers) {
            const res = await getResourceByMavUserId(memberId);
            const resource = res?.resource;
            if (resource?.resourceId) {
              localOptions.push({
                value: resource.resourceId,
                label: `${resource.name} (Team)`,
              });
            }
          }
        }
      }

      if (projectDetails?.prClient) {
        try {
          const clientCPs = await fetchCusVenCpByCusVenType(
            projectDetails.prClient,
          );
          if (Array.isArray(clientCPs)) {
            clientCPs.forEach((cp) => {
              if (cp?.contactPerName && cp?.cusVenId) {
                localOptions.push({
                  value: cp.cusVenId,
                  label: `${cp.contactPerName} (Client Contact)`,
                });
              }
            });
          }
        } catch (error) {
          console.log("No client contact persons found");
        }
      }

      try {
        const partners = await getPartnersByProject(projectId);
        if (Array.isArray(partners)) {
          for (const partner of partners) {
            if (partner?.partnerName) {
              try {
                const partnerCPs = await fetchCusVenCpByCusVenType(
                  partner.partnerName,
                );
                if (Array.isArray(partnerCPs)) {
                  partnerCPs.forEach((cp) => {
                    if (cp?.contactPerName && cp?.cusVenId) {
                      localOptions.push({
                        value: cp.cusVenId,
                        label: `${cp.contactPerName} (Partner Contact)`,
                      });
                    }
                  });
                }
              } catch (error) {
                console.log(
                  `No contact persons for partner: ${partner.partnerName}`,
                );
              }
            }
          }
        }
      } catch (error) {
        console.log("No partners found for project");
      }

      setAssigneeOptionsByProject((prev) => ({
        ...prev,
        [projectId]: localOptions,
      }));
    } catch (error) {
      console.error("Failed to fetch project assignees", error);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      if (!user?.organisationId) return;
      const data = await fetchProjectsByOrg(user.organisationId);
      const options = (data || []).map((p) => ({
        value: p.projectId,
        label: `${p.prTitle} (${p.projectId})`,
      }));
      setProjectOptions(options);
    } catch (err) {
      console.error("Failed to load projects", err);
    }
  };

  // Load project name
  const loadProjectName = async (projectId) => {
    if (!projectId) return;
    try {
      const res = await fetchProjectDetails(projectId);
      setProjectName(res?.prTitle || projectId);
    } catch (error) {
      setProjectName(projectId);
    }
  };

  // Handle edit click
  const handleEditClick = async () => {
    if (ticket?.projectAssigned) {
      const projectIds = ticket.projectAssigned
        .split(",")
        .map((id) => id.trim());
      let allAssignees = [];

      for (const pid of projectIds) {
        let assignees = assigneeOptionsByProject[pid];
        if (!assignees || assignees.length === 0) {
          await fetchProjectAndPartners(pid);
          assignees = assigneeOptionsByProject[pid] || [];
        }
        allAssignees.push(...assignees);
      }

      const unique = new Map();
      allAssignees.forEach((a) => unique.set(a.value, a));
      setTicketAssignees(Array.from(unique.values()));
    }
    setShowEditModal(true);
  };

  // Get all from emails
  const getAllFromEmails = () => {
    if (!messages || messages.length === 0) return "N/A";
    const emails = messages.map((m) => m.fromMail).filter(Boolean);
    const uniqueEmails = [...new Set(emails)];
    return uniqueEmails.join(", ");
  };

  // Filter work logs
  const getWorkLogs = () => {
    return ticketLogs.filter((log) => log.actionType === "WORK_LOG");
  };

  const getNonWorkLogActivities = () => {
    return ticketLogs.filter((log) => log.actionType !== "WORK_LOG");
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "jpg":
      case "jpeg":
      case "png":
        return "🖼️";
      case "zip":
        return "📦";
      default:
        return "📎";
    }
  };

  // Handle add remark
  const handleAddRemark = async () => {
    if (!newRemark.trim()) {
      toast.warning("Remark cannot be empty");
      return;
    }

    try {
      setSavingRemark(true);
      await addTicketRemark({
        ticketId: ticketId,
        orgId: user.organisationId,
        closureRemarks: newRemark,
        actionType: "REMARK",
        createdBy: String(user.userId),
        changedBy: String(user.userId),
      });
      toast.success("Remark added");
      setNewRemark("");
      const updatedRemarks = await getTicketRemarks(ticketId);
      setRemarks(updatedRemarks);
    } catch (err) {
      toast.error("Failed to add remark");
    } finally {
      setSavingRemark(false);
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.downloadAttachment(
        blobUrl,
        fileName,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  // Handle view attachment
  const handleViewAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.viewAttachment(blobUrl, fileName);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error("Failed to view attachment");
    }
  };

  // Collect and fetch all IDs for name resolution
  const collectAndFetchIds = async (details, logs, tasks) => {
    const userIds = new Set();
    const resourceIds = new Set();
    const cpIds = new Set();
    const contactGuids = new Set();

    const addId = (id) => {
      if (!id) return;
      const trimmedId = id.trim();

      if (trimmedId.includes("@")) return;
      if (trimmedId.includes(" ") && !trimmedId.startsWith("CVCP")) return;

      if (trimmedId.startsWith("CVCP")) {
        cpIds.add(trimmedId);
        return;
      }

      if (isValidGuid(trimmedId)) {
        contactGuids.add(trimmedId);
        return;
      }

      const numericId = parseInt(trimmedId);
      if (!isNaN(numericId)) {
        userIds.add(numericId);
        return;
      }

      resourceIds.add(trimmedId);
    };

    if (details?.assignedTo) {
      details.assignedTo.split(",").forEach(addId);
    }
    if (details?.submittedBy) {
      details.submittedBy.split(",").forEach(addId);
    }
    if (details?.createdBy) {
      addId(details.createdBy);
    }

    logs?.forEach((log) => {
      if (log.changedBy) addId(log.changedBy);
    });

    tasks?.forEach((task) => {
      if (task.assignedTo) addId(task.assignedTo);
    });

    await Promise.all([
      userIds.size > 0
        ? fetchResourceUserNames(Array.from(userIds))
        : Promise.resolve(),
      resourceIds.size > 0
        ? fetchResourceNames(Array.from(resourceIds))
        : Promise.resolve(),
      ...Array.from(cpIds).map((id) => resolveCusVenCpName(id)),
      contactGuids.size > 0
        ? fetchContactNames(Array.from(contactGuids))
        : Promise.resolve(),
    ]);
  };

  // Load all data
  const loadData = async () => {
    try {
      setLoading(true);

      const thread = await getTicketThread(ticketId);
      setTicket(thread?.ticket || null);
      setMessages(thread?.messages || []);
      setTicketLogs(thread?.logs || []);

      try {
        const tasks = await getTasksByTicketId(ticketId);
        setTicketTasks(tasks || []);
      } catch (error) {
        setTicketTasks([]);
      }

      const details = await getTicketByTicketId(ticketId);
      setTicketDetails(details);

      if (details?.projectAssigned) {
        await loadProjectName(details.projectAssigned);
      }

      await loadStatusMasterData();
      await resolveAllTicketNames(details);

      if (details?.projectAssigned) {
        const projectIds = details.projectAssigned
          .split(",")
          .map((id) => id.trim());
        for (const pid of projectIds) {
          await fetchProjectAndPartners(pid);
        }
      }

      const tasksList = await getTasksByTicketId(ticketId).catch(() => []);
      await collectAndFetchIds(details, thread?.logs, tasksList);

      try {
        const block = await getBuildingBlockByTicket(ticketId);
        setBuildingBlock(block);
      } catch (error) {
        setBuildingBlock(null);
      }

      const remarksData = await getTicketRemarks(ticketId);
      setRemarks(remarksData || []);

      const attachmentData = await attachmentApi.getTicketAttachments(ticketId);
      if (attachmentData?.attachments) {
        setTicketAttachments(attachmentData.attachments);
      }
    } catch (err) {
      console.error("Failed to load ticket", err);
      toast.error("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.organisationId) {
      buildAssignedToOptions();
      fetchProjects();
      loadData();
    }
  }, [ticketId, user]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  if (!ticket) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Ticket not found</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="tdp-container">
      {/* HEADER */}
      <Card className="tdp-header-card shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={9}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "81vw",
                  alignItems: "center",
                }}
              >
                <h4>{ticket.ticketName}</h4>
                <Button onClick={handleEditClick}>✏ Edit Ticket</Button>
              </div>

              <div className="d-flex gap-2 mt-2">
                <Badge>{ticket.ticketId}</Badge>
                {getStatusBadgeWithName(ticketDetails?.status)}
                {getPriorityBadge(ticket.priority)}
              </div>

              <div className="mt-2 opacity-75">
                Created by <b>{resolveContactName(ticket.createdBy)}</b> •{" "}
                {moment(ticket.ticketTime).format("MMM DD YYYY HH:mm")}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABS */}
      <Card className="tdp-main-card shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="overview">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <Row className="mt-3 g-3">
                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Ticket Information</h6>
                      <Row>
                        <Col sm={6}>
                          <p>
                            <b>Ticket ID</b>
                            <br />
                            {ticketDetails?.ticketId}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Status</b>
                            <br />
                            {getStatusBadgeWithName(ticketDetails?.status)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Sub Status</b>
                            <br />
                            {getSubStatusBadge(ticketDetails?.subStatus)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Priority</b>
                            <br />
                            {getPriorityBadge(ticketDetails?.priority)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Category</b>
                            <br />
                            {resolveCategoryName(ticketDetails?.category)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Ticket Type</b>
                            <br />
                            {resolveTicketType(ticketDetails?.ticketType)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Reason</b>
                            <br />
                            {resolveTicketReason(ticketDetails?.ticketReason)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Project</b>
                            <br />
                            {projectName || "N/A"}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Due Date</b>
                            <br />
                            {ticketDetails?.dueDate
                              ? moment(ticketDetails.dueDate).format(
                                  "MMM DD YYYY",
                                )
                              : "N/A"}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">People & Email</h6>
                      <Row>
                        <Col sm={12}>
                          <p>
                            <b>Created By</b>
                            <br />
                            {resolveContactName(ticketDetails?.createdBy)}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Assigned To</b>
                            <br />
                            {resolveAssigneeNames(ticketDetails?.assignedTo) ||
                              "Unassigned"}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Submitted By</b>
                            <br />
                            {resolveContactName(ticketDetails?.submittedBy) ||
                              "N/A"}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Email Source</b>
                            <br />
                            {getAllFromEmails()}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={12}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Attachments</h6>
                      {loadingAttachments ? (
                        <Spinner />
                      ) : ticketAttachments.length > 0 ? (
                        ticketAttachments.map((att, i) => (
                          <Card key={i} className="mb-2">
                            <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <span
                                  style={{ fontSize: "20px" }}
                                  className="me-2"
                                >
                                  {getFileIcon(att.fileName)}
                                </span>
                                <div>
                                  <div className="fw-bold">{att.fileName}</div>
                                  <small className="text-muted">
                                    Uploaded by {att.createdBy}
                                  </small>
                                </div>
                              </div>
                              <div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() =>
                                    handleViewAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  👁️ View
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  ⬇️ Download
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      ) : (
                        <p className="text-muted">No attachments found</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* REMARKS TAB */}
            <Tab eventKey="remarks" title="Remarks">
              <div className="mt-3">
                <Card className="mb-3">
                  <Card.Body>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add remark..."
                      value={newRemark}
                      onChange={(e) => setNewRemark(e.target.value)}
                    />
                    <div className="text-end mt-2">
                      <Button onClick={handleAddRemark} disabled={savingRemark}>
                        {savingRemark ? "Saving..." : "Add Remark"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
                {remarks.length > 0 ? (
                  remarks.map((remark) => (
                    <Card key={remark.ticketLogGuid} className="mb-2">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>
                              {resolveContactName(remark.createdBy)}
                            </strong>
                            <div className="text-muted small">
                              {formatDateTime(
                                remark.createdDate,
                                remark.createdTime,
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">{remark.closureRemarks}</div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p className="text-muted">No remarks yet</p>
                )}
              </div>
            </Tab>

            {/* CONVERSATION TAB */}
            <Tab eventKey="conversation" title="Conversation">
              <div className="mt-3">
                {messages.length > 0 ? (
                  <Accordion
                    alwaysOpen={false}
                    className="tdp-conversation-accordion"
                  >
                    {messages.map((msg, index) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={msg.messageGuid}
                        className="tdp-message-accordion"
                      >
                        <Accordion.Header>
                          <div className="conversation-header">
                            <div>
                              <strong>{msg.fromName || msg.fromMail}</strong>
                              <div className="text-muted small">
                                {moment(msg.createdOnDate).format(
                                  "MMM DD YYYY",
                                )}{" "}
                                {msg.createdOnTime?.substring(0, 8)}
                              </div>
                            </div>
                            {msg.isInternal && (
                              <Badge bg="warning" className="ms-2">
                                Internal
                              </Badge>
                            )}
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: msg.rawHtmlBody || msg.body,
                            }}
                          />
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                ) : (
                  <p>No messages</p>
                )}
              </div>
            </Tab>

            {/* WORK LOG TAB */}
            <Tab eventKey="worklog" title="Work Log">
              <Table bordered hover className="tdp-activity-table mt-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Effort</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getWorkLogs().length > 0 ? (
                    getWorkLogs().map((log, i) => (
                      <tr key={log.ticketLogGuid}>
                        <td>{i + 1}</td>
                        <td>
                          <Badge bg="info">
                            {log.actionType?.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td>{resolveContactName(log.changedBy)}</td>
                        <td>
                          {log.effortValue
                            ? `${log.effortValue} ${log.effortUnit || "HOURS"}`
                            : "-"}
                        </td>
                        <td>
                          {formatDateTime(log.createdDate, log.createdTime)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No work log entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Tab>

            {/* ACTIVITY LOG TAB */}
            <Tab eventKey="activity" title="Activity Log">
              <Table bordered hover className="tdp-activity-table mt-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>Old</th>
                    <th>New</th>
                    <th>User</th>
                    <th>Effort</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getNonWorkLogActivities().length > 0 ? (
                    getNonWorkLogActivities().map((log, i) => (
                      <tr key={log.ticketLogGuid}>
                        <td>{i + 1}</td>
                        <td>
                          <Badge bg="secondary">
                            {log.actionType?.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td>{log.oldValue || "-"}</td>
                        <td>{log.newValue || "-"}</td>
                        <td>{resolveContactName(log.changedBy)}</td>
                        <td>
                          {log.effortValue
                            ? `${log.effortValue} ${log.effortUnit}`
                            : "-"}
                        </td>
                        <td>
                          {formatDateTime(log.createdDate, log.createdTime)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No activity logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                {ticketTasks.length > 0 ? (
                  ticketTasks.map((task) => (
                    <Card key={task.taskId} className="tdp-task-card mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>{task.taskName}</b>{" "}
                            <Badge bg="dark">{task.taskId}</Badge>
                          </div>
                          <div className="d-flex gap-2">
                            {getPriorityBadge(task.taskPriority)}
                          </div>
                        </div>
                        <div className="text-muted mt-2">
                          Assigned To: {resolveAssigneeNames(task.assignedTo)}
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No related tasks</p>
                )}
              </div>
            </Tab>

            {/* BUILDING BLOCK TAB */}
            <Tab eventKey="buildingBlock" title="Building Block">
              <div className="mt-3">
                {buildingBlock ? (
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>Block ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Project</th>
                        <th>Process POC</th>
                        <th>Tech POC</th>
                        <th>Type</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{buildingBlock.buildingBlockId}</td>
                        <td>{buildingBlock.buildingBlockName}</td>
                        <td>
                          <Badge bg="secondary">
                            {buildingBlock.buildingBlockStatus}
                          </Badge>
                        </td>
                        <td>{buildingBlock.buildingBlockProject}</td>
                        <td>{buildingBlock.buildingBlockProcessPoc}</td>
                        <td>{buildingBlock.buildingBlockTechPoc}</td>
                        <td>{buildingBlock.buildingBlockTypes}</td>
                        <td>
                          {buildingBlock.actualStartDate
                            ? moment(buildingBlock.actualStartDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {buildingBlock.actualEndDate
                            ? moment(buildingBlock.actualEndDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <p>No Building Block mapped</p>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* EDIT MODAL */}
      <TicketEditModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        ticket={ticket}
        orgId={user?.organisationId}
        userDetails={user}
        projectOptions={projectOptions}
        ticketAssignees={ticketAssignees}
        assigneeOptionsByProject={assigneeOptionsByProject}
        fetchProjectAndPartners={fetchProjectAndPartners}
        refreshTickets={loadData}
      />
    </Container>
  );
};

export default TicketInfoPage;

// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import {
//   Container,
//   Card,
//   Row,
//   Col,
//   Badge,
//   Spinner,
//   Alert,
//   Table,
//   Button,
//   Tabs,
//   Tab,
// } from "react-bootstrap";
// import moment from "moment";
// import {
//   getTicketThread,
//   getTasksByTicketId,
//   getTicketByTicketId,
//   getBuildingBlockByTicket,
// } from "../Api/TicketApiOrg";
// import { useAuth } from "../Context/AuthContext";
// import TicketEditModal from "./TicketEditModal";
// import "./TicketInfoPage.css";
// import { attachmentApi } from "../Api/TicketApiOrg";
// import { toast } from "react-toastify";
// import { Accordion } from "react-bootstrap";
// import { addTicketRemark, getTicketRemarks } from "../Api/TicketApiOrg";
// import { getResourceDetails, fetchResourceById } from "../Api/ResourceMaster";
// import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
// import { getTeamById } from "../Api/TeamMaster";
// import { getResourceByMavUserId } from "../Api/ResourceList";
// import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
// import { getPartnersByProject } from "../Api/PartnerMaster";

// import { getResourcesByOrgId } from "../Api/ResourceList";

// import { fetchProjectsByOrg } from "../Api/ViewProjects";

// import { fetchProjectDetails } from "../Api/ViewProjects";
// import {
//   getTicketLoggedTypeByCode,
//   resolveNamesApi,
// } from "../Api/TicketApiOrg";

// /* ================= HELPERS ================= */

// const getPriorityBadge = (priority) => {
//   const variants = {
//     critical: "danger",
//     high: "warning",
//     medium: "primary",
//     low: "secondary",
//     normal: "info",
//   };

//   return (
//     <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
//       {priority || "N/A"}
//     </Badge>
//   );
// };

// const getStatusBadge = (status) => {
//   const variants = {
//     open: "success",
//     closed: "secondary",
//     "in progress": "warning",
//     resolved: "info",
//   };

//   return (
//     <Badge bg={variants[status?.toLowerCase()] || "secondary"}>
//       {status || "N/A"}
//     </Badge>
//   );
// };

// const formatDateTime = (date, time) => {
//   if (!date) return "N/A";
//   return `${moment(date).format("MMM DD, YYYY")} ${
//     time?.substring(0, 8) || ""
//   }`;
// };

// /* ================= PAGE ================= */

// const TicketInfoPage = () => {
//   const { ticketId } = useParams();
//   const { user } = useAuth();

//   const [ticket, setTicket] = useState(null);
//   const [messages, setMessages] = useState([]);
//   const [ticketLogs, setTicketLogs] = useState([]);
//   const [ticketTasks, setTicketTasks] = useState([]);
//   const [attachments, setAttachments] = useState([]);

//   const [ticketDetails, setTicketDetails] = useState(null);

//   const [loading, setLoading] = useState(true);
//   const [showEditModal, setShowEditModal] = useState(false);

//   const [buildingBlock, setBuildingBlock] = useState(null);

//   /* SAFE STATES FOR MODAL */
//   const [projectOptions, setProjectOptions] = useState([]);
//   const [ticketAssignees, setTicketAssignees] = useState([]);

//   const [ticketAttachments, setTicketAttachments] = useState([]);
//   const [loadingAttachments, setLoadingAttachments] = useState(false);

//   const [remarks, setRemarks] = useState([]);
//   const [newRemark, setNewRemark] = useState("");
//   const [savingRemark, setSavingRemark] = useState(false);

//   const [nameRefresh, setNameRefresh] = useState(false);

//   /* State for user names mapping (by mavUserId) */
//   const [userNamesMap, setUserNamesMap] = useState({});

//   /* State for resource names mapping (by resourceId like SSMAHA0007) */
//   const [resourceNamesMap, setResourceNamesMap] = useState({});

//   const [nameMap, setNameMap] = useState({});

//   const getNamesFromIds = (ids) => {
//     if (!ids) return "N/A";

//     const idArray =
//       typeof ids === "string" ? ids.split(",").map((i) => i.trim()) : ids;

//     return idArray.map((id) => nameMap[id] || id).join(", ");
//   };

//   /* State for CusVen CP names mapping */
//   const [cusVenCpCache, setCusVenCpCache] = useState({});

//   const [assignedToOptions, setAssignedToOptions] = useState([]);

//   const [projectName, setProjectName] = useState("");

//   const [ticketReasonName, setTicketReasonName] = useState("");

//   const [assigneeOptionsByProject, setAssigneeOptionsByProject] = useState({});

//   /* ================= RESOLVE NAME FUNCTIONS ================= */

//   const fetchResourceUserNames = async (userIds) => {
//     console.log("🔍 fetchResourceUserNames called with userIds:", userIds);

//     try {
//       const uniqueUserIds = [...new Set(userIds)];
//       const userPromises = uniqueUserIds.map(async (userId) => {
//         try {
//           console.log(`🌐 Fetching user details for mavUserId: ${userId}`);
//           const response = await getResourceDetails({ mavUserId: userId });
//           console.log(`📦 API Response for user ${userId}:`, response);

//           if (response?.resource) {
//             console.log(
//               `✅ Found user name for ${userId}:`,
//               response.resource.name,
//             );
//             return { userId, name: response.resource.name };
//           }
//           return { userId, name: `User ${userId}` };
//         } catch (error) {
//           console.error(`Failed to fetch user ${userId}:`, error);
//           return { userId, name: `User ${userId}` };
//         }
//       });

//       const userResults = await Promise.all(userPromises);
//       const namesMap = {};
//       userResults.forEach(({ userId, name }) => {
//         namesMap[userId] = name;
//       });

//       console.log("📝 Updating userNamesMap with:", namesMap);
//       setUserNamesMap((prev) => ({ ...prev, ...namesMap }));
//     } catch (error) {
//       console.error("Failed to fetch user names:", error);
//     }
//   };

//   const loadTicketReasonName = async (code) => {
//     if (!code) return;

//     try {
//       console.log("📡 Fetching ticket reason for:", code);

//       const res = await getTicketLoggedTypeByCode(code);

//       console.log("✅ Ticket reason details:", res);

//       setTicketReasonName(res?.descriptions || code);
//     } catch (error) {
//       console.error("❌ Failed to fetch ticket reason", error);
//       setTicketReasonName(code); // fallback
//     }
//   };

//   const buildAssignedToOptions = async () => {
//     try {
//       if (!user?.organisationId) return;

//       const res = await getResourcesByOrgId(user.organisationId);

//       const resources = Array.isArray(res) ? res : res?.resources || [];

//       const options = resources.map((r) => ({
//         value: r.resourceId,
//         label: r.name,
//       }));

//       setAssignedToOptions(options);
//     } catch (error) {
//       console.error("❌ Failed to load assignees", error);
//     }
//   };

//   const fetchProjectAndPartners = async (projectId) => {
//     try {
//       if (assigneeOptionsByProject[projectId]) return;

//       const localOptions = [];

//       const projectDetails = await fetchProjectDetails(projectId);
//       console.log("📌 Project Details:", projectDetails);

//       // 🔹 Team members
//       if (projectDetails?.teamAssigned) {
//         const teamData = await getTeamById(projectDetails.teamAssigned);
//         if (Array.isArray(teamData?.teamMembers)) {
//           for (const memberId of teamData.teamMembers) {
//             const res = await getResourceByMavUserId(memberId);
//             const resource = res?.resource;
//             if (resource?.resourceId) {
//               localOptions.push({
//                 value: resource.resourceId,
//                 label: `${resource.name} (Team)`,
//               });
//             }
//           }
//         }
//       }

//       // 🔹 Client Contact Persons
//       if (projectDetails?.prClient) {
//         try {
//           const clientCPs = await fetchCusVenCpByCusVenType(
//             projectDetails.prClient,
//           );
//           if (Array.isArray(clientCPs)) {
//             clientCPs.forEach((cp) => {
//               if (cp?.contactPerName && cp?.cusVenId) {
//                 localOptions.push({
//                   value: cp.cusVenId,
//                   label: `${cp.contactPerName} (Client Contact)`,
//                 });
//               }
//             });
//           }
//         } catch (error) {
//           console.log("No client contact persons found");
//         }
//       }

//       // 🔹 Partners and their contact persons
//       try {
//         const partners = await getPartnersByProject(projectId);
//         if (Array.isArray(partners)) {
//           for (const partner of partners) {
//             if (partner?.partnerName) {
//               try {
//                 const partnerCPs = await fetchCusVenCpByCusVenType(
//                   partner.partnerName,
//                 );
//                 if (Array.isArray(partnerCPs)) {
//                   partnerCPs.forEach((cp) => {
//                     if (cp?.contactPerName && cp?.cusVenId) {
//                       localOptions.push({
//                         value: cp.cusVenId,
//                         label: `${cp.contactPerName} (Partner Contact)`,
//                       });
//                     }
//                   });
//                 }
//               } catch (error) {
//                 console.log(
//                   `No contact persons for partner: ${partner.partnerName}`,
//                 );
//               }
//             }
//           }
//         }
//       } catch (error) {
//         console.log("No partners found for project");
//       }

//       console.log(
//         `📁 Final assignee options for project ${projectId}:`,
//         localOptions,
//       );

//       setAssigneeOptionsByProject((prev) => ({
//         ...prev,
//         [projectId]: localOptions,
//       }));
//     } catch (error) {
//       console.error("❌ Failed to fetch project assignees", error);
//     }
//   };

//   const fetchProjects = async () => {
//     try {
//       if (!user?.organisationId) return;

//       const data = await fetchProjectsByOrg(user.organisationId);

//       const options = (data || []).map((p) => ({
//         value: p.projectId,
//         label: `${p.prTitle} (${p.projectId})`,
//       }));

//       setProjectOptions(options);
//     } catch (err) {
//       console.error("❌ Failed to load projects", err);
//     }
//   };

//   const handleEditClick = async () => {
//     if (ticket?.projectAssigned) {
//       const projectIds = ticket.projectAssigned
//         .split(",")
//         .map((id) => id.trim());

//       let allAssignees = [];

//       for (const pid of projectIds) {
//         let assignees = assigneeOptionsByProject[pid];

//         if (!assignees || assignees.length === 0) {
//           await fetchProjectAndPartners(pid);
//           assignees = assigneeOptionsByProject[pid] || [];
//         }

//         allAssignees.push(...assignees);
//       }

//       const unique = new Map();
//       allAssignees.forEach((a) => unique.set(a.value, a));

//       setTicketAssignees(Array.from(unique.values()));
//     }

//     setShowEditModal(true);
//   };

//   const resolveAssigneeName = (ids) => {
//     if (!ids) return "N/A";

//     const idArray =
//       typeof ids === "string" ? ids.split(",").map((i) => i.trim()) : ids;

//     return idArray
//       .map((id) => {
//         // Email
//         if (id.includes("@")) return id;

//         // Already name
//         if (id.includes(" ") && !id.startsWith("CVCP")) return id;

//         // Contact Person
//         if (id.startsWith("CVCP")) {
//           return cusVenCpCache[id] || id;
//         }

//         // Resource
//         const match = assignedToOptions.find((opt) => opt.value === id);
//         return match ? match.label : id;
//       })
//       .join(", ");
//   };

//   const loadProjectName = async (projectId) => {
//     if (!projectId) return;

//     try {
//       console.log("📡 Fetching project details for:", projectId);

//       const res = await fetchProjectDetails(projectId);

//       console.log("✅ Project details:", res);

//       setProjectName(res?.prTitle || projectId);
//     } catch (error) {
//       console.error("❌ Failed to fetch project details", error);
//       setProjectName(projectId); // fallback
//     }
//   };

//   const fetchResourceNames = async (resourceIds) => {
//     console.log("🔍 fetchResourceNames called with resourceIds:", resourceIds);

//     try {
//       const uniqueResourceIds = [
//         ...new Set(
//           resourceIds.filter(
//             (id) =>
//               id &&
//               !id.startsWith("CVCP") &&
//               !id.includes("@") &&
//               !id.includes(" ") &&
//               isNaN(Number(id)),
//           ),
//         ),
//       ];

//       console.log("📝 Unique resource IDs to fetch:", uniqueResourceIds);

//       const resourcePromises = uniqueResourceIds.map(async (resourceId) => {
//         try {
//           console.log(`🌐 Fetching resource details for ID: ${resourceId}`);
//           const response = await fetchResourceById(resourceId);
//           console.log(`📦 API Response for resource ${resourceId}:`, response);

//           if (response?.resource?.name) {
//             console.log(
//               `✅ Found resource name for ${resourceId}:`,
//               response.resource.name,
//             );
//             return { resourceId, name: response.resource.name };
//           }

//           return { resourceId, name: resourceId };
//         } catch (error) {
//           console.error(`Failed to fetch resource ${resourceId}:`, error);
//           return { resourceId, name: resourceId };
//         }
//       });

//       const resourceResults = await Promise.all(resourcePromises);
//       const namesMap = {};
//       resourceResults.forEach(({ resourceId, name }) => {
//         namesMap[resourceId] = name;
//       });

//       console.log("📝 Updating resourceNamesMap with:", namesMap);
//       setResourceNamesMap((prev) => ({ ...prev, ...namesMap }));
//     } catch (error) {
//       console.error("Failed to fetch resource names:", error);
//     }
//   };

//   const resolveCusVenCpName = async (cpId) => {
//     console.log("🔍 resolveCusVenCpName called with:", cpId);

//     if (!cpId || !cpId.startsWith("CVCP")) return null;

//     if (cusVenCpCache[cpId]) {
//       console.log(`✅ CP ${cpId} found in cache:`, cusVenCpCache[cpId]);
//       return cusVenCpCache[cpId];
//     }

//     try {
//       console.log(`🌐 Fetching CP details for: ${cpId}`);
//       const cp = await fetchCusVenCpById(cpId);
//       console.log(`📦 API Response for CP ${cpId}:`, cp);

//       if (cp?.contactPerName) {
//         console.log(`✅ Found CP name for ${cpId}:`, cp.contactPerName);
//         setCusVenCpCache((prev) => ({
//           ...prev,
//           [cpId]: cp.contactPerName,
//         }));
//         return cp.contactPerName;
//       }
//     } catch (err) {
//       console.error("❌ Failed to fetch CP:", cpId, err);
//     }
//     return cpId;
//   };

//   const fetchTicketNames = async (ticketData) => {
//     console.log("🔴🔴🔴 FETCH TICKET NAMES IS RUNNING 🔴🔴🔴");
//     console.log("🔍 fetchTicketNames called with ticketData:", ticketData);

//     if (!ticketData) {
//       console.log("❌ ticketData is null or undefined");
//       return;
//     }

//     // Log all properties
//     console.log("📋 Full ticketData object keys:", Object.keys(ticketData));

//     // Log field values
//     console.log("📝 ===== TICKET FIELD VALUES =====");
//     console.log("📝 assignedTo value:", ticketData?.assignedTo);
//     console.log("📝 submittedBy value:", ticketData?.submittedBy);
//     console.log("📝 createdBy value:", ticketData?.createdBy);
//     console.log("📝 ==============================");

//     try {
//       const userIds = new Set();
//       const resourceIds = new Set();
//       const cpIds = new Set();

//       const processId = (id, fieldName) => {
//         console.log(`  → Processing ${fieldName}: "${id}"`);

//         if (!id) {
//           console.log(`    → ${fieldName}: empty/null`);
//           return;
//         }

//         const trimmedId = id.trim();
//         console.log(`    → Trimmed: "${trimmedId}"`);

//         if (trimmedId.includes("@")) {
//           console.log(
//             `    → ✅ Email address, will display as-is: ${trimmedId}`,
//           );
//           return;
//         }

//         if (trimmedId.includes(" ") && !trimmedId.startsWith("CVCP")) {
//           console.log(`    → ✅ Name, will display as-is: ${trimmedId}`);
//           return;
//         }

//         if (trimmedId.startsWith("CVCP")) {
//           console.log(`    → ✅ CVCP ID, will fetch from API: ${trimmedId}`);
//           cpIds.add(trimmedId);
//           return;
//         }

//         const numericId = parseInt(trimmedId);
//         if (!isNaN(numericId)) {
//           console.log(
//             `    → ✅ Numeric user ID, will fetch from getResourceDetails: ${numericId}`,
//           );
//           userIds.add(numericId);
//           return;
//         }

//         console.log(
//           `    → ✅ Resource ID, will fetch from fetchResourceById: ${trimmedId}`,
//         );
//         resourceIds.add(trimmedId);
//       };

//       if (ticketData?.assignedTo) {
//         console.log(`📝 Processing assignedTo: "${ticketData.assignedTo}"`);
//         const ids = ticketData.assignedTo.split(",").map((id) => id.trim());
//         console.log(`   Split into:`, ids);
//         ids.forEach((id) => processId(id, "assignedTo"));
//       } else {
//         console.log("⚠️ assignedTo is empty or null");
//       }

//       if (ticketData?.submittedBy) {
//         console.log(`📝 Processing submittedBy: "${ticketData.submittedBy}"`);
//         const ids = ticketData.submittedBy.split(",").map((id) => id.trim());
//         console.log(`   Split into:`, ids);
//         ids.forEach((id) => processId(id, "submittedBy"));
//       } else {
//         console.log("⚠️ submittedBy is empty or null");
//       }

//       if (ticketData?.createdBy) {
//         console.log(`📝 Processing createdBy: "${ticketData.createdBy}"`);
//         processId(ticketData.createdBy, "createdBy");
//       } else {
//         console.log("⚠️ createdBy is empty or null");
//       }

//       console.log("📊 Collected IDs - userIds:", Array.from(userIds));
//       console.log("📊 Collected IDs - resourceIds:", Array.from(resourceIds));
//       console.log("📊 Collected IDs - cpIds:", Array.from(cpIds));

//       if (userIds.size === 0 && resourceIds.size === 0 && cpIds.size === 0) {
//         console.log("⚠️ No IDs to fetch, skipping API calls");
//         return;
//       }

//       console.log("🚀 Starting API calls...");

//       await Promise.all([
//         userIds.size > 0
//           ? fetchResourceUserNames(Array.from(userIds))
//           : Promise.resolve(),
//         resourceIds.size > 0
//           ? fetchResourceNames(Array.from(resourceIds))
//           : Promise.resolve(),
//         ...Array.from(cpIds).map((id) => resolveCusVenCpName(id)),
//       ]);

//       console.log("✅ All name fetches completed");
//       setNameRefresh((prev) => !prev);
//     } catch (error) {
//       console.error("Failed to fetch ticket names:", error);
//     }
//   };

//   const resolveAssigneeNameSync = (ids) => {
//     if (!ids) return "N/A";

//     const idArray =
//       typeof ids === "string" ? ids.split(",").map((i) => i.trim()) : ids;

//     const names = idArray.map((id) => {
//       if (id.includes("@")) {
//         return id;
//       }

//       if (id.includes(" ") && !id.startsWith("CVCP")) {
//         return id;
//       }

//       if (id.startsWith("CVCP")) {
//         return cusVenCpCache[id] || id;
//       }

//       const numericId = parseInt(id);
//       if (!isNaN(numericId)) {
//         return userNamesMap[numericId] || `User ${id}`;
//       }

//       return resourceNamesMap[id] || id;
//     });

//     return names.join(", ");
//   };

//   /* ================= FETCH USER NAMES ================= */

//   const fetchUserNames = async (logs) => {
//     console.log("🔍 fetchUserNames called with logs length:", logs?.length);

//     try {
//       const userIds = [
//         ...new Set(
//           logs
//             .filter((log) => log.changedBy && !isNaN(log.changedBy))
//             .map((log) => parseInt(log.changedBy))
//             .filter((id) => !isNaN(id)),
//         ),
//       ];

//       console.log("📝 Unique user IDs from logs:", userIds);

//       if (userIds.length > 0) {
//         await fetchResourceUserNames(userIds);
//       }
//     } catch (error) {
//       console.error("Failed to fetch user names:", error);
//     }
//   };

//   const getAllFromEmails = () => {
//     if (!messages || messages.length === 0) return "N/A";

//     const emails = messages.map((m) => m.fromMail).filter(Boolean);

//     const uniqueEmails = [...new Set(emails)];

//     return uniqueEmails.join(", ");
//   };

//   /* ================= FILTER FUNCTIONS ================= */

//   const getWorkLogs = () => {
//     return ticketLogs.filter((log) => log.actionType === "WORK_LOG");
//   };

//   const getNonWorkLogActivities = () => {
//     return ticketLogs.filter((log) => log.actionType !== "WORK_LOG");
//   };

//   /* ================= FETCH DATA ================= */

//   const loadData = async () => {
//     console.log("🚀 loadData started for ticketId:", ticketId);

//     try {
//       setLoading(true);

//       console.log("📡 Fetching ticket thread...");
//       const thread = await getTicketThread(ticketId);
//       console.log("✅ Ticket thread received:", thread);

//       setTicket(thread?.ticket || null);
//       setMessages(thread?.messages || []);
//       setTicketLogs(thread?.logs || []);

//       if (thread?.logs && thread.logs.length > 0) {
//         console.log("📝 Fetching user names from logs...");
//         await fetchUserNames(thread.logs);
//       }

//       console.log("📡 Fetching tasks...");
//       try {
//         const tasks = await getTasksByTicketId(ticketId);
//         setTicketTasks(tasks || []);
//         console.log("✅ Tasks received:", tasks);
//       } catch (error) {
//         console.log("⚠️ No tasks found for this ticket (404 is normal)");
//         setTicketTasks([]);
//       }

//       console.log("📡 Fetching ticket details...");
//       const details = await getTicketByTicketId(ticketId);
//       setTicketDetails(details);
//       if (details?.projectAssigned) {
//         await loadProjectName(details.projectAssigned);
//       }
//       console.log("✅ Ticket details received:", details);
//       if (details?.ticketReason) {
//         await loadTicketReasonName(details.ticketReason);
//       }

//       if (details?.projectAssigned) {
//         const projectIds = details.projectAssigned
//           .split(",")
//           .map((id) => id.trim());

//         for (const pid of projectIds) {
//           await fetchProjectAndPartners(pid);
//         }
//       }

//       if (details) {
//         console.log("📝 Fetching names for ticket fields...");
//         const collectIds = (data, logs, tasks) => {
//           const ids = new Set();

//           const addIds = (value) => {
//             if (!value) return;
//             value
//               .toString()
//               .split(",")
//               .forEach((v) => ids.add(v.trim()));
//           };

//           // Ticket fields
//           addIds(data?.assignedTo);
//           addIds(data?.submittedBy);
//           addIds(data?.createdBy);

//           // 🔥 Work logs
//           logs?.forEach((log) => {
//             addIds(log.changedBy);
//           });

//           // 🔥 Tasks
//           tasks?.forEach((task) => {
//             addIds(task.assignedTo);
//           });

//           return Array.from(ids);
//         };

//         const tasks = await getTasksByTicketId(ticketId);
//         setTicketTasks(tasks || []);

//         const ids = collectIds(details, thread?.logs, tasks);

//         if (ids.length > 0) {
//           const resolved = await resolveNamesApi(ids);

//           const map = {};
//           resolved.forEach((item) => {
//             map[item.id] = item.name;
//           });

//           setNameMap(map);
//         }
//       }

//       console.log("📡 Fetching building block...");
//       try {
//         const block = await getBuildingBlockByTicket(ticketId);
//         setBuildingBlock(block);
//         console.log("✅ Building block received:", block);
//       } catch (error) {
//         console.log("⚠️ No building block found for this ticket");
//         setBuildingBlock(null);
//       }

//       console.log("📡 Fetching remarks...");
//       const remarksData = await getTicketRemarks(ticketId);
//       setRemarks(remarksData || []);
//       console.log("✅ Remarks received:", remarksData);

//       console.log("📡 Fetching attachments...");
//       const attachmentData = await attachmentApi.getTicketAttachments(ticketId);
//       console.log("✅ Attachments received:", attachmentData);

//       if (attachmentData?.attachments) {
//         setTicketAttachments(attachmentData.attachments);
//       }
//     } catch (err) {
//       console.error("❌ Failed to load ticket", err);
//       toast.error("Failed to load ticket details");
//     } finally {
//       setLoading(false);
//       console.log("🏁 loadData completed");
//     }
//   };

//   const getFileIcon = (fileName) => {
//     const ext = fileName?.split(".").pop()?.toLowerCase();

//     switch (ext) {
//       case "pdf":
//         return "📄";
//       case "doc":
//       case "docx":
//         return "📝";
//       case "xls":
//       case "xlsx":
//         return "📊";
//       case "jpg":
//       case "png":
//         return "🖼️";
//       case "zip":
//         return "📦";
//       default:
//         return "📎";
//     }
//   };

//   const handleAddRemark = async () => {
//     if (!newRemark.trim()) {
//       toast.warning("Remark cannot be empty");
//       return;
//     }

//     try {
//       setSavingRemark(true);

//       await addTicketRemark({
//         ticketId: ticketId,
//         orgId: user.organisationId,
//         closureRemarks: newRemark,
//         actionType: "REMARK",
//         createdBy: String(user.userId),
//         changedBy: String(user.userId),
//       });

//       toast.success("Remark added");
//       setNewRemark("");

//       const updatedRemarks = await getTicketRemarks(ticketId);
//       setRemarks(updatedRemarks);
//     } catch (err) {
//       toast.error("Failed to add remark");
//     } finally {
//       setSavingRemark(false);
//     }
//   };

//   const handleDownloadAttachment = async (blobUrl, fileName) => {
//     try {
//       const response = await attachmentApi.downloadAttachment(
//         blobUrl,
//         fileName,
//       );

//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute("download", fileName);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       toast.error("Failed to download attachment");
//     }
//   };

//   const handleViewAttachment = async (blobUrl, fileName) => {
//     try {
//       const response = await attachmentApi.viewAttachment(blobUrl, fileName);

//       const blob = new Blob([response.data], {
//         type: response.headers["content-type"],
//       });

//       const url = window.URL.createObjectURL(blob);
//       window.open(url, "_blank");
//       setTimeout(() => window.URL.revokeObjectURL(url), 1000);
//     } catch (error) {
//       toast.error("Failed to view attachment");
//     }
//   };

//   const getUserDisplayName = (userId) => {
//     if (!userId) return "N/A";
//     const userIdNum = parseInt(userId);
//     if (isNaN(userIdNum)) return userId;
//     return userNamesMap[userIdNum] || `User ${userId}`;
//   };

//   useEffect(() => {
//     if (user?.organisationId) {
//       buildAssignedToOptions();
//       fetchProjects(); // ✅ ADD THIS
//       loadData();
//     }
//   }, [ticketId, user]);

//   if (loading) {
//     return (
//       <div className="text-center mt-5">
//         <Spinner />
//       </div>
//     );
//   }

//   if (!ticket) {
//     return (
//       <Container className="mt-4">
//         <Alert variant="danger">Ticket not found</Alert>
//       </Container>
//     );
//   }

//   return (
//     <Container fluid className="tdp-container">
//       {/* HEADER */}
//       <Card className="tdp-header-card shadow-sm mb-4">
//         <Card.Body>
//           <Row className="align-items-center">
//             <Col md={9}>
//               <div
//                 style={{
//                   display: "flex",
//                   justifyContent: "space-between",
//                   width: "81vw",
//                   alignItems: "center",
//                 }}
//               >
//                 <h4>{ticket.ticketName}</h4>
//                 <Button onClick={handleEditClick}>✏ Edit Ticket</Button>
//               </div>

//               <div className="d-flex gap-2 mt-2">
//                 <Badge>{ticket.ticketId}</Badge>
//                 {getStatusBadge(ticket.status)}
//                 {getPriorityBadge(ticket.priority)}
//               </div>

//               <div className="mt-2 opacity-75">
//                 Created by <b>{getNamesFromIds(ticket.createdBy)}</b> •{" "}
//                 {moment(ticket.ticketTime).format("MMM DD YYYY HH:mm")}
//               </div>
//             </Col>

//             <Col md={3} className="text-end"></Col>
//           </Row>
//         </Card.Body>
//       </Card>

//       {/* TABS */}
//       <Card className="tdp-main-card shadow-sm">
//         <Card.Body>
//           <Tabs defaultActiveKey="overview">
//             {/* OVERVIEW */}
//             <Tab eventKey="overview" title="Overview">
//               <Row className="mt-3 g-3">
//                 <Col md={6}>
//                   <Card className="tdp-info-card">
//                     <Card.Body>
//                       <h6 className="mb-3">Ticket Information</h6>
//                       <Row>
//                         <Col sm={6}>
//                           <p>
//                             <b>Ticket ID</b>
//                             <br />
//                             {ticketDetails?.ticketId}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Status</b>
//                             <br />
//                             {getStatusBadge(ticketDetails?.status)}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Priority</b>
//                             <br />
//                             {getPriorityBadge(ticketDetails?.priority)}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Category</b>
//                             <br />
//                             {ticketDetails?.category || "N/A"}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Ticket Type</b>
//                             <br />
//                             {ticketDetails?.ticketType || "N/A"}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Reason</b>
//                             <br />
//                             {ticketReasonName ||
//                               ticketDetails?.ticketReason ||
//                               "N/A"}{" "}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Project</b>
//                             <br />
//                             {projectName || "N/A"}{" "}
//                           </p>
//                         </Col>
//                         <Col sm={6}>
//                           <p>
//                             <b>Due Date</b>
//                             <br />
//                             {ticketDetails?.dueDate
//                               ? moment(ticketDetails.dueDate).format(
//                                   "MMM DD YYYY",
//                                 )
//                               : "N/A"}
//                           </p>
//                         </Col>
//                       </Row>
//                     </Card.Body>
//                   </Card>
//                 </Col>

//                 <Col md={6}>
//                   <Card className="tdp-info-card">
//                     <Card.Body>
//                       <h6 className="mb-3">People & Email</h6>
//                       <Row>
//                         <Col sm={12}>
//                           <p>
//                             <b>Created By</b>
//                             <br />
//                             {getNamesFromIds(ticketDetails?.createdBy)}
//                           </p>
//                         </Col>
//                         <Col sm={12}>
//                           <p>
//                             <b>Assigned To</b>
//                             <br />
//                             {getNamesFromIds(ticketDetails?.assignedTo) ||
//                               "Unassigned"}
//                           </p>
//                         </Col>
//                         <Col sm={12}>
//                           <p>
//                             <b>Submitted By</b>
//                             <br />
//                             {getNamesFromIds(ticketDetails?.submittedBy) ||
//                               "N/A"}
//                           </p>
//                         </Col>
//                         <Col sm={12}>
//                           <p>
//                             <b>Email Source</b>
//                             <br />
//                             {getAllFromEmails()}
//                           </p>
//                         </Col>
//                       </Row>
//                     </Card.Body>
//                   </Card>
//                 </Col>

//                 <Col md={12}>
//                   <Card className="tdp-info-card">
//                     <Card.Body>
//                       <h6 className="mb-3">Attachments</h6>
//                       {loadingAttachments ? (
//                         <Spinner />
//                       ) : ticketAttachments.length > 0 ? (
//                         ticketAttachments.map((att, i) => (
//                           <Card key={i} className="mb-2">
//                             <Card.Body className="py-2 d-flex justify-content-between align-items-center">
//                               <div className="d-flex align-items-center">
//                                 <span
//                                   style={{ fontSize: "20px" }}
//                                   className="me-2"
//                                 >
//                                   {getFileIcon(att.fileName)}
//                                 </span>
//                                 <div>
//                                   <div className="fw-bold">{att.fileName}</div>
//                                   <small className="text-muted">
//                                     Uploaded by {att.createdBy}
//                                   </small>
//                                 </div>
//                               </div>
//                               <div>
//                                 <Button
//                                   variant="outline-primary"
//                                   size="sm"
//                                   className="me-2"
//                                   onClick={() =>
//                                     handleViewAttachment(
//                                       att.azureBlobUrl,
//                                       att.fileName,
//                                     )
//                                   }
//                                 >
//                                   👁️ View
//                                 </Button>
//                                 <Button
//                                   variant="outline-success"
//                                   size="sm"
//                                   onClick={() =>
//                                     handleDownloadAttachment(
//                                       att.azureBlobUrl,
//                                       att.fileName,
//                                     )
//                                   }
//                                 >
//                                   ⬇️ Download
//                                 </Button>
//                               </div>
//                             </Card.Body>
//                           </Card>
//                         ))
//                       ) : (
//                         <p className="text-muted">No attachments found</p>
//                       )}
//                     </Card.Body>
//                   </Card>
//                 </Col>
//               </Row>
//             </Tab>

//             {/* REMARKS TAB */}
//             <Tab eventKey="remarks" title="Remarks">
//               <div className="mt-3">
//                 <Card className="mb-3">
//                   <Card.Body>
//                     <textarea
//                       className="form-control"
//                       rows="3"
//                       placeholder="Add remark..."
//                       value={newRemark}
//                       onChange={(e) => setNewRemark(e.target.value)}
//                     />
//                     <div className="text-end mt-2">
//                       <Button onClick={handleAddRemark} disabled={savingRemark}>
//                         {savingRemark ? "Saving..." : "Add Remark"}
//                       </Button>
//                     </div>
//                   </Card.Body>
//                 </Card>
//                 {remarks.length > 0 ? (
//                   remarks.map((remark) => (
//                     <Card key={remark.ticketLogGuid} className="mb-2">
//                       <Card.Body>
//                         <div className="d-flex justify-content-between">
//                           <div>
//                             <strong>{remark.createdBy}</strong>
//                             <div className="text-muted small">
//                               {formatDateTime(
//                                 remark.createdDate,
//                                 remark.createdTime,
//                               )}
//                             </div>
//                           </div>
//                         </div>
//                         <div className="mt-2">{remark.closureRemarks}</div>
//                       </Card.Body>
//                     </Card>
//                   ))
//                 ) : (
//                   <p className="text-muted">No remarks yet</p>
//                 )}
//               </div>
//             </Tab>

//             {/* CONVERSATION TAB */}
//             <Tab eventKey="conversation" title="Conversation">
//               <div className="mt-3">
//                 {messages.length > 0 ? (
//                   <Accordion
//                     alwaysOpen={false}
//                     className="tdp-conversation-accordion"
//                   >
//                     {messages.map((msg, index) => (
//                       <Accordion.Item
//                         eventKey={index.toString()}
//                         key={msg.messageGuid}
//                         className="tdp-message-accordion"
//                       >
//                         <Accordion.Header>
//                           <div className="conversation-header">
//                             <div>
//                               <strong>{msg.fromName || msg.fromMail}</strong>
//                               <div className="text-muted small">
//                                 {moment(msg.createdOnDate).format(
//                                   "MMM DD YYYY",
//                                 )}{" "}
//                                 {msg.createdOnTime?.substring(0, 8)}
//                               </div>
//                             </div>
//                             {msg.isInternal && (
//                               <Badge bg="warning" className="ms-2">
//                                 Internal
//                               </Badge>
//                             )}
//                           </div>
//                         </Accordion.Header>
//                         <Accordion.Body>
//                           <div
//                             dangerouslySetInnerHTML={{
//                               __html: msg.rawHtmlBody || msg.body,
//                             }}
//                           />
//                         </Accordion.Body>
//                       </Accordion.Item>
//                     ))}
//                   </Accordion>
//                 ) : (
//                   <p>No messages</p>
//                 )}
//               </div>
//             </Tab>

//             {/* WORK LOG TAB */}
//             <Tab eventKey="worklog" title="Work Log">
//               <Table bordered hover className="tdp-activity-table mt-3">
//                 <thead className="table-light">
//                   <tr>
//                     <th>#</th>
//                     <th>Action</th>
//                     <th>User</th>
//                     <th>Effort</th>
//                     <th>Date</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {getWorkLogs().length > 0 ? (
//                     getWorkLogs().map((log, i) => (
//                       <tr key={log.ticketLogGuid}>
//                         <td>{i + 1}</td>
//                         <td>
//                           <Badge bg="info">
//                             {log.actionType?.replace(/_/g, " ")}
//                           </Badge>
//                         </td>
//                         <td>{getNamesFromIds(log.changedBy)}</td>
//                         <td>
//                           {log.effortValue
//                             ? `${log.effortValue} ${log.effortUnit || "HOURS"}`
//                             : "-"}
//                         </td>
//                         <td>
//                           {formatDateTime(log.createdDate, log.createdTime)}
//                         </td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td colSpan="5" className="text-center text-muted">
//                         No work log entries found
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </Table>
//             </Tab>

//             {/* ACTIVITY LOG TAB */}
//             <Tab eventKey="activity" title="Activity Log">
//               <Table bordered hover className="tdp-activity-table mt-3">
//                 <thead className="table-light">
//                   <tr>
//                     <th>#</th>
//                     <th>Action</th>
//                     <th>Old</th>
//                     <th>New</th>
//                     <th>User</th>
//                     <th>Effort</th>
//                     <th>Date</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {getNonWorkLogActivities().length > 0 ? (
//                     getNonWorkLogActivities().map((log, i) => (
//                       <tr key={log.ticketLogGuid}>
//                         <td>{i + 1}</td>
//                         <td>
//                           <Badge bg="secondary">
//                             {log.actionType?.replace(/_/g, " ")}
//                           </Badge>
//                         </td>
//                         <td>{log.oldValue || "-"}</td>
//                         <td>{log.newValue || "-"}</td>
//                         <td>{getNamesFromIds(log.changedBy)}</td>{" "}
//                         <td>
//                           {log.effortValue
//                             ? `${log.effortValue} ${log.effortUnit}`
//                             : "-"}
//                         </td>
//                         <td>
//                           {formatDateTime(log.createdDate, log.createdTime)}
//                         </td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td colSpan="7" className="text-center text-muted">
//                         No activity logs found
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </Table>
//             </Tab>

//             {/* TASKS TAB */}
//             <Tab eventKey="tasks" title="Tasks">
//               <div className="mt-3">
//                 {ticketTasks.length > 0 ? (
//                   ticketTasks.map((task) => (
//                     <Card key={task.taskId} className="tdp-task-card mb-3">
//                       <Card.Body>
//                         <div className="d-flex justify-content-between">
//                           <div>
//                             <b>{task.taskName}</b>{" "}
//                             <Badge bg="dark">{task.taskId}</Badge>
//                           </div>
//                           <div className="d-flex gap-2">
//                             {getStatusBadge(task.taskStatus)}
//                             {getPriorityBadge(task.taskPriority)}
//                           </div>
//                         </div>
//                         <div className="text-muted mt-2">
//                           Assigned To: {getNamesFromIds(task.assignedTo)}{" "}
//                         </div>
//                       </Card.Body>
//                     </Card>
//                   ))
//                 ) : (
//                   <p>No related tasks</p>
//                 )}
//               </div>
//             </Tab>

//             {/* BUILDING BLOCK TAB */}
//             <Tab eventKey="buildingBlock" title="Building Block">
//               <div className="mt-3">
//                 {buildingBlock ? (
//                   <Table bordered hover>
//                     <thead className="table-light">
//                       <tr>
//                         <th>Block ID</th>
//                         <th>Name</th>
//                         <th>Status</th>
//                         <th>Project</th>
//                         <th>Process POC</th>
//                         <th>Tech POC</th>
//                         <th>Type</th>
//                         <th>Start Date</th>
//                         <th>End Date</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       <tr>
//                         <td>{buildingBlock.buildingBlockId}</td>
//                         <td>{buildingBlock.buildingBlockName}</td>
//                         <td>
//                           <Badge bg="secondary">
//                             {buildingBlock.buildingBlockStatus}
//                           </Badge>
//                         </td>
//                         <td>{buildingBlock.buildingBlockProject}</td>
//                         <td>{buildingBlock.buildingBlockProcessPoc}</td>
//                         <td>{buildingBlock.buildingBlockTechPoc}</td>
//                         <td>{buildingBlock.buildingBlockTypes}</td>
//                         <td>
//                           {buildingBlock.actualStartDate
//                             ? moment(buildingBlock.actualStartDate).format(
//                                 "MMM DD YYYY",
//                               )
//                             : "N/A"}
//                         </td>
//                         <td>
//                           {buildingBlock.actualEndDate
//                             ? moment(buildingBlock.actualEndDate).format(
//                                 "MMM DD YYYY",
//                               )
//                             : "N/A"}
//                         </td>
//                       </tr>
//                     </tbody>
//                   </Table>
//                 ) : (
//                   <p>No Building Block mapped</p>
//                 )}
//               </div>
//             </Tab>
//           </Tabs>
//         </Card.Body>
//       </Card>

//       {/* EDIT MODAL */}
//       {/* At the bottom of TicketInfoPage.js */}
//       <TicketEditModal
//         show={showEditModal}
//         onClose={() => setShowEditModal(false)}
//         ticket={ticket}
//         orgId={user?.organisationId}
//         userDetails={user}
//         projectOptions={projectOptions}
//         ticketAssignees={ticketAssignees}
//         assigneeOptionsByProject={assigneeOptionsByProject}
//         fetchProjectAndPartners={fetchProjectAndPartners}
//         refreshTickets={loadData}
//       />
//     </Container>
//   );
// };

// export default TicketInfoPage;
